/**
 * `/downloads` — TASK 10.1 Step 4.
 *
 * The mock's IN YOUR POCKET screen (mock/SteamyMusic.dc.html L500-534), which
 * was a STATIC list over `db.dloads` with a `setInterval` that walked a fake
 * percentage upward (`_dlRetry`, L1097). Everything here reads the real
 * `lib/download-manager.ts` snapshot instead; the markup, the copy and the
 * class names are the mock's, unchanged.
 *
 * ── WHAT THE MOCK GOT RIGHT AND IS KEPT ───────────────────────────────────
 * The five states ui-contract §10.6 lists for `#/downloads?state=…` — empty ·
 * downloading % · failed (Retry) · resuming · quota-warning (`role="alert"`) —
 * all survive, with the mock's exact strings. The quota banner keeps its iOS
 * eviction disclosure verbatim ({@link QUOTA_WARNING_MESSAGE}); it is the only
 * place in the product that tells a user their downloads can vanish without
 * them doing anything, so paraphrasing it would be a regression.
 *
 * ── WHAT THE MOCK GOT WRONG AND IS FIXED ──────────────────────────────────
 *   * The mock's `div[role=progressbar]` carried an `aria-label` and NOTHING
 *     else — no `aria-valuenow`/`min`/`max`, so a screen reader announced
 *     "Total storage used, progress bar" and no value. Both bars now carry the
 *     full value triplet, and the per-item bar (which the mock left unlabelled
 *     entirely) is named after its download.
 *   * Per-item progress was not announced at all. AC 13.1 requires it, so
 *     transitions go to the shell's polite live region — THROTTLED (see
 *     {@link PROGRESS_ANNOUNCE_INTERVAL_MS}): a percentage that ticks a hundred
 *     times per track would make the screen reader unusable for the whole
 *     download, which is worse than not announcing it. Terminal transitions
 *     (done / failed) bypass the throttle, because those are the ones the user
 *     is waiting for.
 *
 * ── §8.5 (never colour alone) ─────────────────────────────────────────────
 * Every state is carried by TEXT, not by the note's colour: "Queued…",
 * "Reconnected — resuming download…", "Download failed — tap Retry." and the
 * size label are distinct strings, and the failed row additionally grows a
 * Retry button that no other state has. The colours are decoration on top.
 *
 * ── DELETE-ALL IS CONFIRMED, AND THE CONFIRM IS NOT THIS VIEW'S JOB ────────
 * AC 13.3 requires a confirmation; ui-contract §"Delete all downloads
 * confirmation" pins it to `role="dialog"` / `aria-label="Delete all
 * downloads?"`. That dialog belongs to the shell's focus-trapping OverlayStack,
 * which this view cannot reach — so the view calls the injected
 * `onDeleteAllRequest` and the composition root opens the real `ConfirmModal`.
 * The view never deletes anything the root has not confirmed.
 *
 * @module
 */

import { TESTIDS, deleteDownloadButton } from '@streamymusic/shared';

import { append, el } from '../../components/primitives/dom.js';

/** mock L505 — verbatim, including the iOS eviction disclosure (AC 13.6). */
export const QUOTA_WARNING_MESSAGE =
  "⚠ STORAGE NEARLY FULL — this download would blow past your browser's storage quota, " +
  "so it wasn't started. Free some space first. " +
  '(Heads-up: iOS may evict downloads under storage pressure.)';

/** mock L512 — the zero-items empty state. */
export const DOWNLOADS_EMPTY_HEADLINE = 'NO DOWNLOADS YET';

/**
 * The empty-state guidance.
 *
 * ── WHY IT IS NOT THE MOCK'S STRING VERBATIM (Product W2) ─────────────────
 * The mock reads 'Use "Download" on any album, playlist or track to keep it
 * offline.' Two of those three affordances did not exist: only the ALBUM
 * action sheet offered Download, so the first thing a new user read on this
 * screen was instructions they could not follow. TASK 10.1's repair added the
 * TRACK affordance (`modules/app/index.js#requestTrackDownload`), which is why
 * tracks stayed in the copy — but PLAYLISTS are genuinely unwired, and naming
 * them would keep the same lie in a smaller font.
 *
 * The copy therefore describes exactly what the product does. When playlist
 * downloads land, this string grows the word back.
 */
export const DOWNLOADS_EMPTY_MESSAGE = 'Use "Download" on any album or track to keep it offline.';

/** mock L527 — the per-status note strings. */
export const STATUS_NOTES = Object.freeze({
  queued: 'Queued…',
  resuming: 'Reconnected — resuming download…',
  failed: 'Download failed — tap Retry.',
  downloading: '',
  done: '',
});

/**
 * Per-status modifier on the note line (Designer W1).
 *
 * The mock's `noteCol` mapped a colour per status — failed `#FF3E8E`, resuming
 * `#FFD337`, everything else `#8E7FA6` — and `components.css` reserved the
 * `.sm-download-note` hook with a comment saying "colour is dynamic per
 * status", but nothing ever set a class, so every note rendered in the
 * inherited colour. §8.5 was still satisfied (the STRINGS are distinct, and a
 * failed row grows a Retry button no other state has), so this is fidelity
 * rather than an accessibility failure — but the module header asserted "the
 * colours are decoration on top" and there were no colours.
 *
 * `done`/`downloading` are absent deliberately: they have no note text.
 */
export const NOTE_STATUS_CLASS = Object.freeze({
  failed: 'sm-download-note--failed',
  resuming: 'sm-download-note--resuming',
  queued: 'sm-download-note--queued',
});

/** Every modifier, so a repaint can clear the previous one. */
export const NOTE_STATUS_CLASSES = Object.freeze(Object.values(NOTE_STATUS_CLASS));

/**
 * Floor between two PROGRESS announcements. Terminal transitions ignore it.
 * 8 s is roughly the time a screen reader takes to speak one of these lines
 * plus a breath, so the region is never speaking over itself.
 */
export const PROGRESS_ANNOUNCE_INTERVAL_MS = 8_000;

/** mock L1091 — `(tot/1024).toFixed(2)+' GB'`, but over the REAL quota. */
export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
  const mb = bytes / (1024 * 1024);
  if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

/**
 * The storage line. With no quota (a runtime that will not report one) the
 * denominator is omitted rather than invented — "2.1 GB used" is true,
 * "2.1 GB / 8 GB" would not be.
 *
 * @param {number} usedBytes
 * @param {number|null} quotaBytes
 * @returns {string}
 */
export function storageLabel(usedBytes, quotaBytes) {
  if (quotaBytes === null || quotaBytes <= 0) return `${formatBytes(usedBytes)} used`;
  return `${formatBytes(usedBytes)} / ${formatBytes(quotaBytes)}`;
}

/** Whole-percent share of the quota, clamped. */
export function storagePercent(usedBytes, quotaBytes) {
  if (quotaBytes === null || quotaBytes <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((usedBytes / quotaBytes) * 100)));
}

/**
 * The mock's per-type tag colour (`_downloads`: Album -> yellow, Playlist ->
 * teal, Track -> purple).
 *
 * @param {'Album'|'Playlist'|'Track'} type
 * @returns {string}
 */
function tagBackground(type) {
  if (type === 'Album') return 'var(--sm-color-yellow)';
  if (type === 'Playlist') return 'var(--sm-color-teal)';
  return 'var(--sm-color-purple)';
}

/**
 * The line the live region speaks for one row. Exported because it IS the
 * AC 13.1 contract and is asserted directly.
 *
 * @param {import('../../../lib/download-manager.js').DownloadSnapshotRow} row
 * @returns {string}
 */
export function progressAnnouncement(row) {
  const title = row.item.title;
  if (row.status === 'done') return `${title} downloaded`;
  if (row.status === 'failed') return `${title} download failed`;
  if (row.status === 'queued') return `${title} queued for download`;
  if (row.status === 'resuming') return `${title} resuming download`;
  return `${title} downloading, ${String(row.percent)} percent`;
}

/**
 * Builds the `/downloads` section and starts loading its snapshot.
 *
 * @param {{
 *   manager: import('../../../lib/download-manager.js').DownloadManager,
 *   heading: HTMLElement,
 *   onDeleteAllRequest: (confirm: () => void) => void,
 *   quotaWarning?: () => boolean,
 *   announce?: (text: string) => void,
 *   toast?: (text: string, tone?: 'ok'|'err'|'info') => unknown,
 *   now?: () => number,
 *   warn?: (message: string, detail: Record<string, string>) => void,
 * }} options
 *   `onDeleteAllRequest` receives the function to run once the user confirms —
 *   see the module header for why the dialog lives in the composition root.
 *   `quotaWarning` is read at every repaint so a refusal raised from another
 *   screen (an album's Download button) is visible when the user lands here.
 * @returns {{ element: HTMLElement, dispose: () => void, refresh: () => Promise<void> }}
 */
export function DownloadsView(options) {
  const {
    manager,
    heading,
    onDeleteAllRequest,
    quotaWarning = () => false,
    announce,
    toast,
    now = () => Date.now(),
    warn = (message) => {
      console.warn(message);
    },
  } = options;

  const section = el('section', { attrs: { 'data-screen-label': 'Downloads' } });

  /* ── skeleton, built once; only the bodies swap ───────────────────────── */

  const quotaBanner = el('div', {
    className: 'sm-banner sm-banner--quota',
    text: QUOTA_WARNING_MESSAGE,
    // role=alert, per ui-contract's Downloads state list — this one IS an
    // interruption: the user pressed Download and nothing happened.
    attrs: { role: 'alert', hidden: true },
  });

  const storageValue = el('span', { className: 'sm-storage-head__value' });
  const storageFill = el('div', {
    className: 'sm-progress__fill',
    attrs: {
      role: 'progressbar',
      'aria-label': 'Total storage used',
      'aria-valuemin': '0',
      'aria-valuemax': '100',
      'aria-valuenow': '0',
    },
    styleProps: { width: '0%' },
  });
  const storagePanel = el('div', { className: 'sm-panel sm-panel--storage' });
  append(
    storagePanel,
    append(
      el('div', { className: 'sm-storage-head' }),
      el('span', { text: 'Storage used' }),
      storageValue,
    ),
    append(el('div', { className: 'sm-progress' }), storageFill),
  );

  const emptyBlock = el('div', { className: 'sm-empty', attrs: { role: 'status', hidden: true } });
  append(
    emptyBlock,
    el('div', { className: 'sm-glyph sm-glyph--empty-sm', text: DOWNLOADS_EMPTY_HEADLINE }),
    el('p', { className: 'sm-body sm-body--empty', text: DOWNLOADS_EMPTY_MESSAGE }),
  );

  const list = el('div', { className: 'sm-stack-8' });

  const deleteAll = el('button', {
    className: 'sm-btn sm-btn--panel-danger sm-btn--danger-block',
    text: 'Delete all downloads…',
    testId: TESTIDS.DELETE_ALL_DOWNLOADS_BUTTON,
    attrs: { type: 'button', hidden: true },
    on: {
      click: () => {
        // AC 13.3 — the root opens the focus-trapped confirm; only its
        // callback deletes anything.
        onDeleteAllRequest(() => {
          void manager
            .removeAll()
            .then(() => {
              toast?.('All downloads deleted', 'ok');
              return refresh();
            })
            .catch(() => {
              toast?.('Couldn’t delete downloads.', 'err');
            });
        });
      },
    },
  });

  append(section, heading, quotaBanner, storagePanel, emptyBlock, list, deleteAll);

  /* ── announcement throttle (AC 13.1) ──────────────────────────────────── */

  /** Last announced status per item id — only TRANSITIONS are spoken. */
  const spokenStatus = new Map();
  let lastProgressAnnounceAt = 0;

  /** @param {import('../../../lib/download-manager.js').DownloadSnapshotRow} row */
  function maybeAnnounce(row) {
    if (announce === undefined) return;
    const previous = spokenStatus.get(row.item.id);
    const terminal = row.status === 'done' || row.status === 'failed';
    if (previous === row.status && !terminal) {
      // Same non-terminal state: only the percentage moved. Throttled.
      if (row.status !== 'downloading') return;
      if (now() - lastProgressAnnounceAt < PROGRESS_ANNOUNCE_INTERVAL_MS) return;
    }
    if (previous === row.status && terminal) return;
    spokenStatus.set(row.item.id, row.status);
    lastProgressAnnounceAt = now();
    announce(progressAnnouncement(row));
  }

  /* ── rows ─────────────────────────────────────────────────────────────── */

  /**
   * Mounted rows, keyed by item id — see {@link paint} for why this view
   * cannot rebuild its list (Designer/Frontend/Performance C4).
   *
   * @type {Map<string, MountedRow>}
   */
  const mountedRows = new Map();

  /**
   * @typedef {object} MountedRow
   * @property {HTMLElement} card
   * @property {HTMLElement} head
   * @property {HTMLElement} sizeCell
   * @property {HTMLElement} progressRow
   * @property {HTMLElement} progressFill
   * @property {HTMLElement} progressPercent
   * @property {HTMLElement} note
   * @property {HTMLElement} deleteButton
   * @property {HTMLElement|null} retryButton
   * @property {string} status the status the nodes were last painted FROM
   * @property {number} percent
   */

  /**
   * Deletes one row, then puts focus somewhere that still exists (C4).
   *
   * A button that vanishes under the user's finger drops focus to `<body>`,
   * which sends a screen-reader's virtual cursor back to the top of the page
   * and leaves a keyboard user with no idea where they were. WCAG 2.4.3.
   *
   * @param {string} itemId
   * @param {readonly string[]} order the item ids as currently rendered
   */
  function removeRow(itemId, order) {
    /*
     * Pick the successor BEFORE the delete, while the list still exists: the
     * next row's Delete button, else the previous row's, else the Delete-all
     * button, else the heading. Every one of those is a real, focusable thing
     * in the same region — the user lands where the deleted row was, not at
     * the top of the document.
     */
    const index = order.indexOf(itemId);
    const neighbourId = order[index + 1] ?? order[index - 1] ?? null;

    void manager
      .remove(itemId)
      .then(() => {
        toast?.('Download removed', 'ok');
        spokenStatus.delete(itemId);
        return refresh();
      })
      .then(() => {
        const neighbour = neighbourId === null ? null : mountedRows.get(neighbourId);
        /*
         * The heading is the last resort and it IS focusable: every route's
         * `<h1>` comes from `router.js#PageHeading`, which always emits
         * `tabindex="-1"` precisely so the shell can move focus to it. That
         * dependency is invisible from this file, hence this note — if the
         * heading ever loses the attribute, this fallback silently becomes a
         * no-op and focus falls to `<body>`.
         */
        const target =
          neighbour?.deleteButton ?? (deleteAll.hasAttribute('hidden') ? heading : deleteAll);
        // `isConnected`: a second delete (or a navigation) may have landed
        // while the first was in flight, and focusing a detached node throws
        // focus to `<body>` — the exact outcome this is here to prevent.
        if (target.isConnected) target.focus();
      })
      .catch(() => {
        toast?.('Couldn’t remove that download.', 'err');
      });
  }

  /**
   * Builds one download row — mock L516-529. Called ONCE per item; every
   * subsequent snapshot patches the nodes it returns rather than replacing
   * them ({@link patchRow}).
   *
   * @param {import('../../../lib/download-manager.js').DownloadSnapshotRow} row
   * @param {() => readonly string[]} order current render order, read lazily
   *   so a Delete pressed later sees the list as it is THEN
   * @returns {MountedRow}
   */
  function buildRow(row, order) {
    const { item } = row;
    const card = el('div', { className: 'sm-panel sm-panel--flat' });
    const head = el('div', { className: 'sm-flex-row-10' });

    const text = el('span', { className: 'sm-flex-fill' });
    append(
      text,
      /*
       * AC 13.2's WAY IN (QA/Product C3). `/downloads` used to offer Retry and
       * Delete and nothing else — no play control, no track list, no route to
       * one — so the feature's headline capability ("downloaded content plays
       * in airplane mode") had no reachable entry point in the UI at all.
       *
       * A LINK to the album, not a play button: the album screen already has
       * the real transport, the per-track availability markers that keep AC
       * 13.4 honest, the cover art and the favourites. Duplicating a reduced
       * player here would be a second playback surface to keep correct. The
       * album screen renders offline from the local catalog cache — see
       * `AlbumDetail.js`'s offline fallback.
       */
      item.albumId === null
        ? el('span', { className: 'sm-line-primary', text: item.title })
        : el('a', {
            className: 'sm-line-primary sm-line-primary--link',
            text: item.title,
            attrs: {
              href: `#/albums/${encodeURIComponent(item.albumId)}`,
              'aria-label': `Open ${item.title}`,
            },
          }),
      el('span', { className: 'sm-line-secondary', text: item.sub }),
    );

    const sizeCell = el('span', { className: 'sm-line-trailing' });
    const deleteButton = el('button', {
      className: 'sm-btn sm-btn--icon sm-btn--icon-danger',
      text: '🗑',
      // The contract's per-item shape: aria-label "Delete <title>" ->
      // `delete-<slug>-button` (e.g. `delete-in-rainbows-button`). Derived,
      // never written out — nothing in this repo hardcodes a testid.
      testId: deleteDownloadButton(item.title),
      attrs: { type: 'button', 'aria-label': `Delete ${item.title}` },
      on: {
        click: () => {
          removeRow(item.id, order());
        },
      },
    });

    append(
      head,
      el('span', {
        className: 'sm-pill',
        text: item.type.toUpperCase(),
        styleProps: { background: tagBackground(item.type) },
      }),
      text,
      sizeCell,
      deleteButton,
    );
    append(card, head);

    const progressFill = el('div', {
      className: 'sm-progress__fill sm-progress__fill--teal',
      attrs: {
        role: 'progressbar',
        'aria-label': `Download progress for ${item.title}`,
        'aria-valuemin': '0',
        'aria-valuemax': '100',
        'aria-valuenow': '0',
      },
      styleProps: { width: '0%' },
    });
    const progressPercent = el('span', { className: 'sm-progress-row__pct', text: '0%' });
    const progressRow = el('div', { className: 'sm-progress-row', attrs: { hidden: true } });
    append(
      progressRow,
      append(el('div', { className: 'sm-progress sm-progress--10' }), progressFill),
      progressPercent,
    );
    append(card, progressRow);

    const note = el('div', { className: 'sm-download-note', attrs: { hidden: true } });
    append(card, note);

    /** @type {MountedRow} */
    const mounted = {
      card,
      head,
      sizeCell,
      progressRow,
      progressFill,
      progressPercent,
      note,
      deleteButton,
      retryButton: null,
      status: '',
      percent: -1,
    };
    patchRow(mounted, row);
    return mounted;
  }

  /**
   * Updates one mounted row IN PLACE (C4).
   *
   * ── WHY THIS EXISTS ───────────────────────────────────────────────────
   * `paint` used to do `list.replaceChildren(...rows.map(Row))`, and `refresh`
   * fires from `onChange` on every status change AND every megabyte flushed.
   * So every `<button>` in this list was destroyed and rebuilt roughly once a
   * second for the entire duration of a download: a keyboard or screen-reader
   * user focused on Retry or Delete had that element removed from under them
   * over and over, focus fell to `<body>`, and the AT virtual cursor reset to
   * the top of the page each time. WCAG 2.1 SC 2.4.3 (Focus Order) and 3.2.5
   * (Change on Request).
   *
   * Nodes are therefore created once and only their CONTENT changes. The Retry
   * button is added or removed only on a real status transition — not on a
   * percentage tick — so a focused Retry survives every intermediate update.
   * `AlbumDetail.js` establishes this pattern in this codebase; this follows it.
   *
   * @param {MountedRow} mounted
   * @param {import('../../../lib/download-manager.js').DownloadSnapshotRow} row
   * @returns {void}
   */
  function patchRow(mounted, row) {
    const { item, status, percent } = row;

    // mock: the size shows only once the item is complete.
    const size = status === 'done' ? formatBytes(row.bytes) : '';
    if (mounted.sizeCell.textContent !== size) mounted.sizeCell.textContent = size;

    if (percent !== mounted.percent || status !== mounted.status) {
      mounted.percent = percent;
      if (status === 'downloading') {
        mounted.progressRow.removeAttribute('hidden');
        mounted.progressFill.style.setProperty('width', `${String(percent)}%`);
        mounted.progressFill.setAttribute('aria-valuenow', String(percent));
        mounted.progressPercent.textContent = `${String(percent)}%`;
      } else {
        mounted.progressRow.setAttribute('hidden', '');
      }
    }

    if (status !== mounted.status) {
      const previous = mounted.status;
      mounted.status = status;

      /*
       * The Retry button is structural, so it moves ONLY here — on a real
       * transition. Rebuilding it on a progress tick is what used to destroy
       * focus; adding it on the transition INTO `failed` is the moment the
       * user actually needs it.
       */
      if (status === 'failed' && mounted.retryButton === null) {
        const retry = el('button', {
          className: 'sm-btn sm-btn--retry sm-btn--xs',
          text: 'Retry',
          testId: TESTIDS.RETRY_BUTTON,
          attrs: { type: 'button', 'aria-label': `Retry ${item.title}` },
          on: {
            click: () => {
              toast?.('Retrying download…', 'ok');
              void manager
                .retry(item.id)
                .then(refresh)
                .catch(() => {
                  toast?.('Couldn’t retry that download.', 'err');
                });
            },
          },
        });
        mounted.retryButton = retry;
        // Before the Delete button, which is always last in the head row.
        mounted.head.insertBefore(retry, mounted.deleteButton);
      } else if (status !== 'failed' && mounted.retryButton !== null) {
        /*
         * Leaving `failed` removes the button. If it HAD focus (the user just
         * pressed it), hand focus to the Delete button in the same row rather
         * than letting it fall to `<body>` — the row is still there and so is
         * the user's place in it.
         */
        const hadFocus = mounted.retryButton.ownerDocument.activeElement === mounted.retryButton;
        mounted.retryButton.remove();
        mounted.retryButton = null;
        if (hadFocus && mounted.deleteButton.isConnected) mounted.deleteButton.focus();
      }

      const note = STATUS_NOTES[status] ?? '';
      if (note === '') {
        mounted.note.setAttribute('hidden', '');
        mounted.note.textContent = '';
      } else {
        mounted.note.textContent = note;
        mounted.note.removeAttribute('hidden');
      }
      /*
       * §8.5 — the STATE is carried by the text above; this class only tints
       * it. The mock maps a colour per status (`noteCol`: failed #FF3E8E,
       * resuming #FFD337, else #8E7FA6) and `components.css` reserved the hook
       * and said "colour is dynamic per status" — but no modifier was ever
       * emitted, so all three notes rendered in the inherited colour while the
       * module header claimed "the colours are decoration on top". They are
       * decoration on top; now they exist.
       */
      mounted.note.classList.remove(...NOTE_STATUS_CLASSES);
      const toneClass = NOTE_STATUS_CLASS[status];
      if (toneClass !== undefined) mounted.note.classList.add(toneClass);
      if (previous !== '') mounted.card.dataset.status = status;
      else mounted.card.dataset.status = status;
    }
  }

  /* ── paint ────────────────────────────────────────────────────────────── */

  let disposed = false;
  /** Monotonic guard: a slow snapshot must not paint over a newer one. */
  let generation = 0;

  /** @param {import('../../../lib/download-manager.js').DownloadSnapshot} snapshot */
  function paint(snapshot) {
    const { rows, usedBytes, quotaBytes } = snapshot;

    /*
     * DESIGNER W2 — `role="alert"` fires on content INSERTION or CHANGE, not
     * on an unhide. A node whose text predates the live region and is merely
     * revealed with `hidden` is announced inconsistently, and VoiceOver
     * frequently does not announce it at all. The toast path covers the common
     * case (the user pressed Download and got a toast), but the
     * contract-supported deep link `#/downloads?state=quota-warning` produced a
     * banner that shouted visually and said nothing to a screen reader.
     *
     * Setting `textContent` AT THE MOMENT OF UNHIDE is the insertion the role
     * needs. Guarded so a repaint of an already-standing banner does not
     * re-announce it every progress tick.
     */
    const warnNow = quotaWarning();
    if (warnNow) {
      if (quotaBanner.hasAttribute('hidden')) {
        quotaBanner.textContent = QUOTA_WARNING_MESSAGE;
        quotaBanner.removeAttribute('hidden');
      }
    } else {
      quotaBanner.setAttribute('hidden', '');
      quotaBanner.textContent = '';
    }

    /*
     * FRONTEND/PRODUCT W4 — the number under "Storage used" is now the SAME
     * number the quota gate decides on.
     *
     * It used to render `totalBytes` (the sum of `receivedBytes` over `done`
     * tracks — this app's downloads, and nothing else) against the real
     * `usage.quotaBytes`, while `quotaVerdict` gated on the origin's true
     * `usedBytes`. So a user could read "0.4 GB / 8 GB" directly above a
     * `role="alert"` saying STORAGE NEARLY FULL, and both were telling the
     * truth about different things. `totalBytes` is still correct for the
     * per-row size labels, which is where it stayed.
     */
    storageValue.textContent = storageLabel(usedBytes, quotaBytes);
    const percent = storagePercent(usedBytes, quotaBytes);
    storageFill.style.setProperty('width', `${String(percent)}%`);
    storageFill.setAttribute('aria-valuenow', String(percent));

    if (rows.length === 0) {
      emptyBlock.removeAttribute('hidden');
      deleteAll.setAttribute('hidden', '');
      list.replaceChildren();
      mountedRows.clear();
      spokenStatus.clear();
      return;
    }
    emptyBlock.setAttribute('hidden', '');
    deleteAll.removeAttribute('hidden');

    /*
     * KEYED RECONCILIATION (C4). Nodes are added and removed only on a
     * MEMBERSHIP change; everything else is patched in place, so a focused
     * Retry or Delete survives the ~1 Hz progress updates of a running
     * download. See `patchRow` for the full reasoning.
     */
    const order = rows.map((row) => row.item.id);
    const present = new Set(order);
    for (const [id, mounted] of mountedRows) {
      if (present.has(id)) continue;
      mounted.card.remove();
      mountedRows.delete(id);
    }

    const readOrder = () => order;
    let previous = null;
    for (const row of rows) {
      const id = row.item.id;
      let mounted = mountedRows.get(id);
      if (mounted === undefined) {
        mounted = buildRow(row, readOrder);
        mountedRows.set(id, mounted);
      } else {
        patchRow(mounted, row);
      }
      // Position only when it is actually wrong — `insertBefore` on a node
      // that is already there still detaches and re-attaches it, which is
      // exactly the focus loss this whole path exists to avoid.
      const expected = previous === null ? list.firstChild : previous.nextSibling;
      if (expected !== mounted.card) list.insertBefore(mounted.card, expected);
      previous = mounted.card;
    }

    for (const row of rows) maybeAnnounce(row);
  }

  async function refresh() {
    const own = (generation += 1);
    try {
      const snapshot = await manager.snapshot();
      if (disposed || own !== generation) return;
      paint(snapshot);
    } catch (error) {
      if (disposed) return;
      // Never silent: a Downloads screen that cannot read its own store is a
      // real fault, and the user is looking straight at it.
      warn('DownloadsView: could not read the download snapshot', {
        reason: error instanceof Error ? error.name : 'unknown',
      });
      toast?.('Couldn’t read your downloads.', 'err');
    }
  }

  void refresh();

  return {
    element: section,
    refresh,
    dispose() {
      disposed = true;
    },
  };
}
